import api from './api';
import { Customer } from './customers';
import { Team } from './teams';
import { Staff } from './staffs';

export interface Invoice {
  id: number;
  invoice_number: string;
}

export interface Task {
  id: number;
  customer_id?: number;
  task_type_id?: number;
  year?: number;
  task_template_id?: number;
  quotation_id?: number;
  taskType?: {
    id: number;
    name: string;
  };
  taskTemplate?: {
    id: number;
    name: string;
  };
  customer?: Customer;
  amount?: string | number;
}

export interface Quotation {
  id: number;
  quotation_number: string;
  date: string;
  invoice_id?: number;
  customer_id?: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  agent_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'draft' | 'Pending' | 'Approved';
  is_sent?: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  agent?: Staff;
  team?: Team;
  staff?: Staff;
  invoice?: Invoice;
  tasks?: Task[];
  items?: QuotationItem[];
  status_logs?: StatusLog[];
}

export interface QuotationItem {
  id?: number;
  quotation_id?: number;
  task_type_id?: number;
  year?: number;
  task_template_id?: number;
  unit?: string;
  price_per_unit?: number;
  discount?: number;
  amount?: number;
  sort_order?: number;
  taskType?: {
    id: number;
    name: string;
  };
  taskTemplate?: {
    id: number;
    name: string;
  };
}

export interface StatusLog {
  id: number;
  portal: 'admin' | 'customer';
  module: string;
  reference_id: number;
  from_status: string | null;
  to_status: string;
  updated_by?: number | null;
  updated_at: string;
  created_at: string;
  updater?: {
    id: number;
    name: string;
  };
}

export interface CreateQuotationData {
  quotation_number: string;
  date: string;
  invoice_id?: number;
  customer_id?: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  agent_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'draft' | 'Pending' | 'Approved';
  is_sent?: boolean;
  items?: QuotationItem[];
}

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export const quotationService = {
  async getAll() {
    const response = await api.get('/quotations');
    return response.data;
  },

  async getById(id: number): Promise<Quotation> {
    const response = await api.get(`/quotations/${id}`);
    return unwrap<Quotation>(response);
  },

  async create(data: CreateQuotationData): Promise<Quotation> {
    const response = await api.post('/quotations', data);
    return unwrap<Quotation>(response);
  },

  async update(id: number, data: Partial<CreateQuotationData>): Promise<Quotation> {
    const response = await api.put(`/quotations/${id}`, data);
    return unwrap<Quotation>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/quotations/${id}`);
  },

  async approve(id: number): Promise<Quotation> {
    const response = await api.post(`/quotations/${id}/approve`);
    return unwrap<Quotation>(response);
  },

  async getNextNumber(date?: string): Promise<string> {
    const response = await api.get('/quotations/next-number', {
      params: date ? { date } : {},
    });
    return response.data?.data?.quotation_number || response.data?.quotation_number;
  },

  async getPdf(id: number): Promise<Blob> {
    const response = await api.get(`/quotations/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
